// v1.9.28 — Admin-initiated Lenco charge modal.
//
// Opens from a member's profile in the admin portal. Admin picks fund +
// amount + (optionally) an override phone; backend prepares a payment_request
// tied to the target member; the Lenco widget fires the mobile-money prompt
// on the member's phone. The member must approve with their own PIN — admin
// can never charge without member consent.
//
// After launch the modal polls /local-status/:reference every 5s while the
// prompt is pending, so admin sees "✓ Payment received — CR-xxxx created"
// as soon as the webhook / verify lands.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiZap, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { launchLencoWidget } from '../services/lencoWidget';
import { initiateLencoForMember, getLencoLocalStatus, getFundCategories } from '../services/api';

const AdminLencoChargeModal = ({ member, onClose, onSuccess }) => {
  const [funds, setFunds] = useState([]);
  const [fundId, setFundId] = useState('');
  const [amount, setAmount] = useState('');
  const [phone, setPhone] = useState(member?.phone || member?.whatsapp || '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState('form'); // form | waiting | done | failed
  const [reference, setReference] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [crNumber, setCrNumber] = useState(null);
  const pollTimer = useRef(null);
  const pollExpires = useRef(0);

  useEffect(() => {
    getFundCategories().then(r => {
      const list = (r.data || []).filter(f => f.active !== 0);
      setFunds(list);
      // Default to the member's most-common recent fund if we can find it,
      // otherwise the first active fund.
      const monthly = list.find(f => /ወርሓዊ|monthly|contribution/i.test(f.name || '')) || list[0];
      if (monthly) setFundId(String(monthly.id));
    }).catch(() => {});
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, []);

  const displayName = useMemo(() => {
    if (!member) return '';
    return [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(' ').trim();
  }, [member]);

  const stopPolling = () => { if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; } };

  const startPolling = (ref) => {
    stopPolling();
    pollExpires.current = Date.now() + 4 * 60 * 1000; // 4 min hard cap
    pollTimer.current = setInterval(async () => {
      if (Date.now() > pollExpires.current) {
        stopPolling();
        setPhase('failed');
        setStatusMsg('Timed out — no response from the member within 4 minutes. Nothing was charged.');
        return;
      }
      try {
        const r = await getLencoLocalStatus(ref);
        if (r.data.status === 'approved') {
          stopPolling();
          setCrNumber(r.data.cr_number || null);
          setPhase('done');
          setStatusMsg(`Payment received — ${r.data.cr_number ? `receipt ${r.data.cr_number} created` : 'CR being created'}.`);
          try { onSuccess?.(r.data); } catch (_) {}
        } else if (r.data.status === 'rejected') {
          stopPolling();
          setPhase('failed');
          setStatusMsg(r.data.rejection_reason || 'Payment was declined or cancelled on the member\'s phone.');
        }
      } catch (_) { /* keep polling on transient errors */ }
    }, 5000);
  };

  const launch = async () => {
    if (!fundId) return alert('Pick a fund.');
    const k = parseFloat(amount);
    if (!(k > 0)) return alert('Enter an amount greater than 0.');
    setSaving(true);
    try {
      const r = await initiateLencoForMember({
        member_id: member.id,
        amount_kwacha: k,
        fund_category_id: fundId,
        notes: notes || null,
        phone_override: phone && phone !== (member.phone || member.whatsapp) ? phone : null,
      });
      const cfg = r.data;
      setReference(cfg.reference);
      setPhase('waiting');
      setStatusMsg(`Prompt sent to ${cfg.phone}. Ask the member to approve on their phone within 2 minutes.`);
      await launchLencoWidget({
        publicKey: cfg.public_key,
        scriptUrl: cfg.widget_script_url,
        reference: cfg.reference,
        amount: cfg.amount,
        email: cfg.email,
        firstName: cfg.firstName,
        lastName: cfg.lastName,
        phone: cfg.phone,
        onSuccess: () => { /* poll picks it up */ },
        onConfirmationPending: () => setStatusMsg('Member is entering their PIN — waiting for confirmation…'),
        onClose: () => { /* keep polling — user may have closed widget but payment still processing */ },
      });
      startPolling(cfg.reference);
    } catch (e) {
      setPhase('failed');
      setStatusMsg(e.response?.data?.error || 'Could not initiate the payment.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={phase === 'form' ? onClose : undefined} style={{ padding: 16 }}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiZap style={{ color: '#4f46e5' }} /> Charge via Lenco Pay</h3>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>{displayName}{member?.card_number ? ` · ${member.card_number}` : ''}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><FiX /></button>
        </div>

        <div className="modal-body">
          {phase === 'form' && (
            <>
              <div className="form-group">
                <label>Fund <span className="required">*</span></label>
                <select value={fundId} onChange={e => setFundId(e.target.value)}>
                  <option value="">— Select fund —</option>
                  {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Amount (K) <span className="required">*</span></label>
                <input type="number" min="1" step="1" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 1000" autoFocus />
              </div>
              <div className="form-group">
                <label>Member's phone <span className="required">*</span></label>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+260 97 xxx xxxx" />
                <small style={{ color: '#6b7280' }}>The Lenco prompt goes here. Fix it if the number on file is wrong.</small>
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Phone-call reminder" />
              </div>
            </>
          )}
          {phase === 'waiting' && (
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>⏳</div>
              <div style={{ fontSize: 14, color: '#374151', marginBottom: 6, fontWeight: 600 }}>{statusMsg}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Do NOT close this window until the member has approved. Ref: <code style={{ fontFamily: 'Courier New, monospace' }}>{reference}</code></div>
            </div>
          )}
          {phase === 'done' && (
            <div style={{ padding: '16px 0', textAlign: 'center', color: '#166534' }}>
              <FiCheckCircle style={{ fontSize: 48, marginBottom: 10 }} />
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{statusMsg}</div>
              {crNumber && <div style={{ fontSize: 13, color: '#4b5563' }}>The member's subscription owed pill will update automatically.</div>}
            </div>
          )}
          {phase === 'failed' && (
            <div style={{ padding: '16px 0', textAlign: 'center', color: '#b91c1c' }}>
              <FiAlertTriangle style={{ fontSize: 48, marginBottom: 10 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>{statusMsg}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {phase === 'form' && (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={launch} disabled={saving || !fundId || !amount || !phone}>
                {saving ? 'Sending…' : 'Send prompt to phone'}
              </button>
            </>
          )}
          {phase !== 'form' && (
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLencoChargeModal;
